import * as React from 'react';
import { Alert, CircularProgress, Typography } from '@mui/material';

import {
  ClosableModal,
  PrimaryOutlinedButton,
} from 'components';
import { ICertificate } from '../../../../interface';
import { getCertificateTemplateMeta } from './template-catalog';
import {
  buildCertificateRenderRequest,
  getCertificatePreviewFrameHeight,
  renderCertificatePreviewHtml,
} from './utils';
import './styles.scss';

interface Props {
  open: boolean;
  onClose: () => void;
  certificate: ICertificate;
  sessionTitle?: string;
  moduleTitles?: string[];
  enterprise?: {
    entLogo?: string | null;
    logo?: string | null;
    name?: string | null;
  } | null;
}

const DEFAULT_SESSION_TITLE = 'Session Title';

const CertificatePreviewModal = ({
  open,
  onClose,
  certificate,
  sessionTitle = DEFAULT_SESSION_TITLE,
  moduleTitles = [],
  enterprise = null,
}: Props) => {
  const templateMeta = getCertificateTemplateMeta(certificate.template);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const renderRequest = React.useMemo(
    () => buildCertificateRenderRequest({
      certificate,
      enterprise,
      sessionTitle,
      moduleTitles,
    }),
    [certificate, enterprise, moduleTitles, sessionTitle],
  );
  const previewResult = React.useMemo(() => {
    if (!open) {
      return { html: '', error: null as string | null };
    }

    try {
      return {
        html: renderCertificatePreviewHtml(renderRequest),
        error: null,
      };
    } catch (error) {
      return {
        html: '',
        error: error instanceof Error ? error.message : 'Failed to load certificate preview',
      };
    }
  }, [open, renderRequest]);
  const { html: previewHtml, error } = previewResult;
  const isLoadingPreview = false;

  // This will be integrated to BE pdf generation
  const handleDownloadPdf = () => {
    // if (!previewHtml) {
    //   return;
    // }

    // setIsDownloading(true);
    // const previousFrame = document.getElementById(PRINT_FRAME_ID);
    // previousFrame?.remove();

    // const printFrame = document.createElement('iframe');
    // printFrame.id = PRINT_FRAME_ID;
    // printFrame.setAttribute('aria-hidden', 'true');
    // printFrame.style.position = 'fixed';
    // printFrame.style.right = '0';
    // printFrame.style.bottom = '0';
    // printFrame.style.width = '0';
    // printFrame.style.height = '0';
    // printFrame.style.border = '0';
    // printFrame.style.opacity = '0';
    // document.body.appendChild(printFrame);

    // const cleanup = () => {
    //   window.setTimeout(() => {
    //     printFrame.remove();
    //     setIsDownloading(false);
    //   }, 250);
    // };

    // printFrame.onload = () => {
    //   const frameWindow = printFrame.contentWindow;

    //   if (!frameWindow) {
    //     cleanup();
    //     return;
    //   }

    //   frameWindow.onafterprint = cleanup;

    //   window.setTimeout(() => {
    //     try {
    //       frameWindow.focus();
    //       frameWindow.print();
    //     } catch (printError) {
    //       cleanup();
    //     }
    //   }, 150);
    // };

    // const frameDocument = printFrame.contentDocument;

    // if (!frameDocument) {
    //   cleanup();
    //   return;
    // }

    // frameDocument.open();
    // frameDocument.write(buildPrintableHtml(previewHtml));
    // frameDocument.close();
  };

  const frameHeight = getCertificatePreviewFrameHeight(certificate.template);
  let previewBody: React.ReactNode = null;

  if (isLoadingPreview) {
    previewBody = (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <CircularProgress />
      </div>
    );
  } else if (previewHtml) {
    previewBody = (
      <iframe
        title="Certificate preview"
        srcDoc={previewHtml}
        style={{
          width: '100%',
          height: `${frameHeight}px`,
          border: '1px solid rgba(15, 23, 42, 0.12)',
          borderRadius: '16px',
          background: '#fff',
        }}
      />
    );
  }

  return (
    <ClosableModal
      open={open}
      onClose={onClose}
      title={`${templateMeta.shortLabel} preview`}
      subTitle="Preview your generated certificate before proceeding."
      modalClass="certificate-preview-modal"
    >
      <div className="certificate-preview-modal__content">
        <div className="certificate-preview-modal__toolbar">
          <div>
            <Typography variant="body2" color="text.secondary">
              {templateMeta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {certificate.title || 'Certificate of Completion'}
            </Typography>
          </div>
          <PrimaryOutlinedButton
            type="button"
            onClick={handleDownloadPdf}
            disabled={isLoadingPreview || isDownloading || !previewHtml}
          >
            {isDownloading ? 'Preparing PDF...' : 'Download PDF'}
          </PrimaryOutlinedButton>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {previewBody}
      </div>
    </ClosableModal>
  );
};

CertificatePreviewModal.defaultProps = {
  sessionTitle: DEFAULT_SESSION_TITLE,
  moduleTitles: [],
  enterprise: null,
};

export default CertificatePreviewModal;
